// `extension-iframe-utils-broadcast.interface.ng1.ts` + the `IUserAuthStateChangeParams`
// half of `extension-auth.service.ts` — the one message that travels on
// `BroadcastChannelTopic.IFrameChannel` so far.
//
// Hand-written rather than extracted: `tools/extract` takes the enums that both upstreams
// declare, and this one lives only in careero's tree. The three enums it leans on
// (`BroadcastChannelTopic`, `UserAuthStateChangeEvent`, `UserAuthStateChangeOrigin`) ARE
// generated — import them from there, never retype a member.
import type { BroadcastMessage } from './broadcast-channel.ts'
import type { IUserAuthStateChangeParams } from './messages.ts'

/**
 * Upstream's `IFrameBroadcastChannelMessageType`, transcribed with its casing intact.
 *
 * ⚠️ THE CASING IS NOT UNIFORM AND NOT A TYPO. `ReloadState` and `UserAuthStateChange` are
 * PascalCase while `goToState`, `getSingleListener` and `getSingleListenerReply` are camel.
 * These strings go over a bus shared with pages running the AngularJS build, so a tidied
 * `userAuthStateChange` would be dropped in silence by every surface that is not this port.
 */
export const IFrameBroadcastChannelMessageType = {
  ReloadState: 'ReloadState',
  GoToState: 'goToState',
  UserAuthStateChange: 'UserAuthStateChange',
  GetSingleListener: 'getSingleListener',
  GetSingleListenerReply: 'getSingleListenerReply',
} as const
export type IFrameBroadcastChannelMessageType =
  (typeof IFrameBroadcastChannelMessageType)[keyof typeof IFrameBroadcastChannelMessageType]

// `IUserAuthStateChangeParams` is in `messages.ts` already — it is the payload of the
// EXTERNAL message the SPA sends the worker, and the same shape travels on this channel.
// Upstream shares the one interface between both for exactly that reason.

/** `IUserAuthStateChangeIFrameBroadcastChannelMessage`: the envelope's `data` is the params. */
export interface UserAuthStateChangeMessage extends BroadcastMessage {
  type: typeof IFrameBroadcastChannelMessageType.UserAuthStateChange
  data: IUserAuthStateChangeParams
}

/**
 * Narrow an arbitrary message off the channel.
 *
 * ⚠️ IT CHECKS `data`, NOT JUST `type`. Four other message types share this topic and three
 * of them carry a differently-shaped `data`; a receiver that switched on `type` alone would
 * read `params.event` off a `goToState` payload and navigate somewhere undefined.
 */
export function isUserAuthStateChange(
  // `data` is declared optional rather than left off, so that a caller may pass one of the
  // richer message shapes as a literal — a bare `BroadcastMessage` parameter rejects every
  // one of them at the call site for carrying the payload the guard exists to inspect.
  message: BroadcastMessage & { data?: unknown },
): message is UserAuthStateChangeMessage {
  if (message.type !== IFrameBroadcastChannelMessageType.UserAuthStateChange) return false
  const data = message.data as Partial<IUserAuthStateChangeParams> | undefined
  return typeof data === 'object' && data !== null && typeof data.event === 'string'
}
